import * as React from "react";

export interface EditableRow {
    id: number;
    isEditing: boolean;
    isCreated?: boolean;
}

export type RowContent<T extends EditableRow> = Omit<T, "id" | "isEditing">;

export interface EditableRowsOptions<T extends EditableRow> {
    createEmptyRow: (id: number) => T;
    isRowComplete: (row: T) => boolean;
    trimRow: (row: T) => T;
}

export interface EditableRows<T extends EditableRow> {
    rows: T[];
    selectedIds: number[];
    createRowId: () => number;
    replaceRows: (rows: T[]) => void;
    addRow: () => void;
    updateRow: <K extends keyof RowContent<T>>(id: number, field: K, value: T[K]) => void;
    editRow: (id: number) => void;
    confirmRow: (id: number) => void;
    cancelEdit: (id: number) => void;
    deleteRows: (ids: number[]) => void;
    reopenRows: (ids: number[]) => void;
    restoreRows: (rows: T[]) => void;
    duplicateRows: (ids: number[], prepareCopy: (row: T) => T) => void;
    toggleSelected: (id: number, isSelected: boolean) => void;
    toggleAll: (rows: T[], isSelected: boolean) => void;
    areAllSelected: (rows: T[]) => boolean;
}

// Manage rows of a table that are edited inline: adding, editing, confirming, cancelling, deleting, and selecting.
export const useEditableRows = <T extends EditableRow>(options: EditableRowsOptions<T>): EditableRows<T> => {
    const [rows, setRows] = React.useState<T[]>([]);
    const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
    // Row ids are only used on the client, so they never need to match anything on the server.
    const nextIdRef = React.useRef(1);
    // Cancelling an edit restores the row as it was before editing. Rows without a snapshot are new and get removed.
    const editSnapshotRef = React.useRef<Record<number, T>>({});

    const createRowId = () => nextIdRef.current++;

    const replaceRows = (newRows: T[]) => {
        editSnapshotRef.current = {};
        setRows(newRows);
        setSelectedIds([]);
    };

    const addRow = () => {
        const row = { ...options.createEmptyRow(createRowId()), isEditing: true, isCreated: true };
        setRows((previous) => [...previous, row]);
    };

    const updateRow = <K extends keyof RowContent<T>>(id: number, field: K, value: T[K]) => {
        setRows((previous) => previous.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    };

    const editRow = (id: number) => {
        const row = rows.find((candidate) => candidate.id === id);
        if (row) {
            editSnapshotRef.current[id] = { ...row };
        }
        setRows((previous) =>
            previous.map((candidate) => (candidate.id === id ? { ...candidate, isEditing: true } : candidate))
        );
    };

    const deleteRows = (ids: number[]) => {
        ids.forEach((id) => delete editSnapshotRef.current[id]);
        setRows((previous) => previous.filter((row) => !ids.includes(row.id)));
        setSelectedIds((previous) => previous.filter((id) => !ids.includes(id)));
    };

    const reopenRows = (ids: number[]) => {
        setRows((previous) => previous.map((row) => (ids.includes(row.id) ? { ...row, isEditing: true } : row)));
    };

    const restoreRows = (rowsToRestore: T[]) => {
        setRows((previous) => [...previous, ...rowsToRestore.map((row) => ({ ...row, isEditing: true }))]);
    };

    const confirmRow = (id: number) => {
        const row = rows.find((candidate) => candidate.id === id);
        if (!row || !options.isRowComplete(row)) {
            return;
        }
        delete editSnapshotRef.current[id];
        setRows((previous) =>
            previous.map((candidate) =>
                candidate.id === id ? { ...options.trimRow(candidate), isEditing: false } : candidate
            )
        );
    };

    const cancelEdit = (id: number) => {
        const snapshot = editSnapshotRef.current[id];
        if (!snapshot) {
            deleteRows([id]);
            return;
        }
        delete editSnapshotRef.current[id];
        setRows((previous) => previous.map((row) => (row.id === id ? { ...snapshot, isEditing: false } : row)));
    };

    // The copies may need fields cleared that refer to the original, such as a server-side identifier.
    const duplicateRows = (ids: number[], prepareCopy: (row: T) => T) => {
        const copies = rows
            .filter((row) => ids.includes(row.id))
            .map((row) => ({ ...prepareCopy(row), id: createRowId() }));
        setRows((previous) => [...previous, ...copies]);
        setSelectedIds([]);
    };

    const toggleSelected = (id: number, isSelected: boolean) => {
        setSelectedIds((previous) => (isSelected ? [...previous, id] : previous.filter((other) => other !== id)));
    };

    const toggleAll = (rowsToToggle: T[], isSelected: boolean) => {
        const idsToToggle = rowsToToggle.map((row) => row.id);
        setSelectedIds((previous) =>
            isSelected
                ? [...previous, ...idsToToggle.filter((id) => !previous.includes(id))]
                : previous.filter((id) => !idsToToggle.includes(id))
        );
    };

    const areAllSelected = (rowsToCheck: T[]) =>
        rowsToCheck.length > 0 && rowsToCheck.every((row) => selectedIds.includes(row.id));

    return {
        rows,
        selectedIds,
        createRowId,
        replaceRows,
        addRow,
        updateRow,
        editRow,
        confirmRow,
        cancelEdit,
        deleteRows,
        reopenRows,
        restoreRows,
        duplicateRows,
        toggleSelected,
        toggleAll,
        areAllSelected,
    };
};
